import { useCallback, useMemo } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeCodeState } from '~/types';

export type TypeWorkPlanItem = {
  /** 근무일자 (yyyy-MM-dd) */
  workDate: string;
  /** 사전등록 근무유형 */
  preregisteredWorkType: TypeCodeState;
  /** 사전등록 근무계획 상세상태 */
  preregisteredWorkPlanDetailStatus: TypeCodeState;
  /** 근무계획 유형 */
  workType: TypeCodeState;
  /** 근무계획 상태 */
  workPlanStatus: TypeCodeState;
  /** 출근 상태 */
  commuteStatus: string;
  /** 클러스터 코드 */
  cluster: string;
  /** 클러스터 (표시용) */
  displayableCluster: TypeCodeState;
  /** 물류센터 코드 */
  center: string;
  /** 물류센터 (표시용) */
  displayableCenter: TypeCodeState;
  /** 업무파트 코드 */
  workPart: string;
  /** 업무파트 (표시용) */
  displayableWorkPart: TypeCodeState;
  /** 팀명 */
  teamName: string;
  /** 근무시간대 */
  workShift: string;
  /** 근무계획 사전등록 일시 */
  preregisteredAt: string;
  /** 근무계획 등록일시 (yyyy-MM-dd'T'HH:mm:ss) */
  registeredAt: string;
  /** 근무시작일시 */
  workStartedAt: string;
  /** 근무종료일시 */
  workEndedAt: string;
};

type TypeGetMonthlyWorkPlansResponse = {
  code: string;
  message: string;
  data: {
    workPlans: { [workDate: string]: TypeWorkPlanItem };
  };
};

/** @description 월별 근무계획 조회 */
export const useGetMonthlyWorkPlans = ({ yearMonth }: { yearMonth: string }) => {
  const { data } = useSuspenseQuery({
    queryKey: ['work-plans-monthly', yearMonth],
    queryFn: async () => {
      const endpoint = `/work-plans/monthly/${yearMonth}`;
      return await axiosInstance.get<TypeGetMonthlyWorkPlansResponse>(endpoint);
    },
    gcTime: 0,
    retry: 1,
    select: (response) => response.data.data.workPlans ?? {},
  });

  /** 근무계획이 존재하는 날짜 목록 */
  const workPlanDates = useMemo(() => Object.keys(data), [data]);

  /** 특정 날짜의 근무계획 반환 */
  const getWorkPlanByDate = useCallback((workDate: string) => data[workDate] ?? null, [data]);

  return { workPlans: data, workPlanDates, getWorkPlanByDate };
};
